import React from 'react'
import { DEPOSIT_RAMP, PAYOUT_RAMP } from '../../Dashboard/core/seriesColors'
import {
  FLOW_FLAG_LABEL,
  PAYOUT_KIND_COLOR,
  PAYOUT_KIND_INK,
  PAYOUT_KIND_LABEL,
  v2ReasonLabel,
} from '../core/consts'
import { duration, hours, money, shortMsk } from '../core/format'
import { TimelineAdmission, TimelinePayout } from '../core/response.types'
import { DetailRow, DetailSection, FillBar } from './Detail'

const WIDTH = 340

type Props = {
  payout: TimelinePayout
  merchant: string
  method: string
  nowMs: number
}

const pct = (v: number | undefined) => (v === undefined ? '—' : `${Math.round(v * 100)}%`)

/** Отказ второго контура: почему ядро не взяло выплату и на каких условиях взяло бы. */
const AdmissionSection: React.FC<{ reason: string | null; a: TimelineAdmission }> = ({
  reason,
  a,
}) => (
  <DetailSection title='Отказ второго контура'>
    <DetailRow label='Причина'>{v2ReasonLabel(reason)}</DetailRow>
    {a.plan_value !== undefined && (
      <DetailRow label='Ценность плана'>
        {money(a.plan_value)} при пороге {money(a.bar_value)}
      </DetailRow>
    )}
    {a.p_close !== undefined && <DetailRow label='Шанс закрыть'>{pct(a.p_close)}</DetailRow>}
    {a.pressure !== undefined && <DetailRow label='Давление рынка'>{pct(a.pressure)}</DetailRow>}
    {a.inflow !== undefined && (
      <DetailRow label='Подходящих депозитов за SLA'>
        ~{money(Math.round(a.inflow * 10) / 10)}
      </DetailRow>
    )}
    {a.claims !== undefined && <DetailRow label='Выплат на том же рынке'>{a.claims}</DetailRow>}
    <DetailRow label='Ползунок приёма'>{a.admit_dial}</DetailRow>
    <DetailRow label='Растяжка'>
      {a.stretch_tried ? `пробовалась, ценность ${money(a.stretch_plan_value)}` : 'не пробовалась'}
    </DetailRow>
    {a.required_horizon_min !== undefined && (
      <DetailRow label='Взяли бы при сроке'>
        {a.required_horizon_min > 0 ? duration(a.required_horizon_min * 60000) : 'нет срока'}
      </DetailRow>
    )}
    {a.required_chunks !== undefined && (
      <DetailRow label='Взяли бы при частях'>
        {a.required_chunks > 0 ? a.required_chunks : 'нет'}
      </DetailRow>
    )}
  </DetailSection>
)

/** Подсказка бара: кто, сколько прожила, как шёл мэтчинг, с каким конфигом. */
const PayoutTooltip: React.FC<Props> = ({ payout: p, merchant, method, nowMs }) => {
  const startMs = Date.parse(p.start)
  const endMs = p.end ? Date.parse(p.end) : nowMs
  const refused = p.kind === 'rejected'
  const cfg = p.config

  return (
    <div className='card shadow p-3 fs-8' style={{ width: WIDTH }}>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <div>
          <div className='fs-5 fw-bold lh-1'>{money(p.amount)}</div>
          <div className='text-muted mt-1'>
            {p.payout_ticket_id !== null ? `Выплата #${p.payout_ticket_id}` : 'Попытка входа'} ·{' '}
            {merchant}
          </div>
        </div>
        <span
          className='badge fw-semibold text-wrap text-end'
          style={{
            background: PAYOUT_KIND_COLOR[p.kind],
            color: PAYOUT_KIND_INK[p.kind],
            maxWidth: 150,
          }}
        >
          {PAYOUT_KIND_LABEL[p.kind]}
        </span>
      </div>

      <DetailSection title='Заявка'>
        <DetailRow label='Метод'>{method}</DetailRow>
        {p.requested_amount != null && p.requested_amount !== p.amount && (
          <DetailRow label='Запрошено'>{money(p.requested_amount)}</DetailRow>
        )}
        {p.transaction_id && <DetailRow label='ID у мерчанта'>{p.transaction_id}</DetailRow>}
        <DetailRow label='Токен'>{p.token ?? '—'}</DetailRow>
      </DetailSection>

      <DetailSection title={refused ? 'Жила бы' : 'Жизнь в пуле'}>
        <DetailRow label='С'>{shortMsk(p.start)}</DetailRow>
        <DetailRow label='По'>{p.end ? shortMsk(p.end) : 'в работе'}</DetailRow>
        <DetailRow label={refused ? 'SLA' : 'Прожила'}>{duration(endMs - startMs)}</DetailRow>
      </DetailSection>

      {refused && p.admission ? (
        <AdmissionSection reason={p.reason} a={p.admission} />
      ) : refused ? (
        <DetailSection title='Отказ на входе'>
          <DetailRow label='Причина'>{p.reason ?? '—'}</DetailRow>
          {p.amount_group && <DetailRow label='Корзина'>{p.amount_group}</DetailRow>}
          {p.active_count != null && (
            <DetailRow label='Активных в корзине'>
              {p.active_count}
              {p.group_limit != null ? ` из ${p.group_limit}` : ''}
            </DetailRow>
          )}
          <DetailRow label='Флаги'>
            {p.raised_flags && p.raised_flags.length > 0
              ? p.raised_flags.map((f) => FLOW_FLAG_LABEL[f] ?? f).join(', ')
              : '—'}
          </DetailRow>
        </DetailSection>
      ) : (
        <DetailSection title='Мэтчинг'>
          <DetailRow label='Чанки'>
            {p.chunks_live ?? 0} в работе · {p.chunks_paid ?? 0} оплачено
            {cfg?.chunk_limit ? ` · лимит ${cfg.chunk_limit}` : ''}
          </DetailRow>
          {(p.chunks_total ?? 0) > (p.chunks_live ?? 0) && (
            <DetailRow label='Отпало'>{p.chunks_total - p.chunks_live}</DetailRow>
          )}
          <FillBar
            total={p.amount}
            paid={p.paid_amount ?? 0}
            live={p.live_amount ?? 0}
            paidColor={PAYOUT_RAMP[4]}
            liveColor={DEPOSIT_RAMP[1]}
          />
          <DetailRow label='Оплачено'>{money(p.paid_amount)}</DetailRow>
          <DetailRow label='Занято депозитами'>{money(p.live_amount)}</DetailRow>
          <DetailRow label='Осталось смэтчить'>{money(p.remainder)}</DetailRow>
        </DetailSection>
      )}

      {cfg && (
        <DetailSection
          title={cfg.from_snapshot ? 'Конфиг выплаты (снапшот)' : 'Конфиг профиля (текущий)'}
        >
          <DetailRow label='Профиль'>{cfg.profile ?? 'по умолчанию'}</DetailRow>
          <DetailRow label='Лимит чанков'>{cfg.chunk_limit ?? '—'}</DetailRow>
          <DetailRow label='Таймаут выплаты'>{hours(cfg.payout_timeout_hours)}</DetailRow>
          <DetailRow label='Авто-остановка'>
            {cfg.auto_halt_hours ? hours(cfg.auto_halt_hours) : 'выключена'}
          </DetailRow>
          <DetailRow label='Мин. закрывающий'>{money(cfg.min_deposit_amount)}</DetailRow>
          <DetailRow label='Кастомизация'>
            −{money(cfg.customization_down)} / +{money(cfg.customization_up)}
          </DetailRow>
          {cfg.v2 && (
            <>
              <DetailRow label='SLA второго контура'>{hours(cfg.v2.sla_hours)}</DetailRow>
              <DetailRow label='Растяжка'>
                {cfg.v2.stretched ? `+${hours(cfg.v2.stretch_hours)}` : 'нет'}
              </DetailRow>
              <DetailRow label='Обслуживается до'>{shortMsk(cfg.v2.serve_until)}</DetailRow>
            </>
          )}
        </DetailSection>
      )}
    </div>
  )
}

export default PayoutTooltip
